<template>
  <div class="followers-page">
    <b-card no-body class="p-50">
      <div v-if="isLoading" class="text-center py-2">
        <b-spinner label="Loading followers..." />
      </div>
      <b-alert v-else-if="loadError" show variant="danger">
        {{ loadError }}
        <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchFollowers">Retry</b-button>
      </b-alert>
      <div v-else-if="!followers.length" class="text-center text-muted py-2">
        No followers yet.
      </div>
      <template v-else>
        <b-table :items="followers" :fields="fields" responsive class="followers-table mb-0" tbody-tr-class="f-row">
          <template #cell(no)="data">
            <span>{{ String(data.item.no).padStart(2, '0') }}</span>
          </template>

          <template #cell(name)="data">
            <div class="d-flex align-items-center">
              <b-avatar size="26" :src="data.item.avatar" class="mr-1" />
              <span>{{ data.item.name || 'Unknown user' }}</span>
            </div>
          </template>

          <template #cell(actions)>
            <div class="d-flex align-items-center">
              <feather-icon icon="MailIcon" size="15" class="mx-50 text-muted cursor-pointer" />
              <feather-icon icon="Trash2Icon" size="15" class="mx-50 text-muted cursor-pointer" />
            </div>
          </template>
        </b-table>

        <AdminPaginationBar
          :page="currentPage"
          :per-page="perPage"
          :total="totalRows"
          @change="onPageChange"
        />
      </template>
    </b-card>
  </div>
</template>

<script>
import { BAlert, BAvatar, BButton, BCard, BSpinner, BTable } from 'bootstrap-vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import { formatAdminDate, normalizePagination, resolveMediaUrl, unwrapAdminResponse } from '@/utils/adminApiHelpers'

export default {
  components: {
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BSpinner,
    BTable,
    AdminPaginationBar,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      currentPage: 1,
      perPage: 8,
      totalRows: 0,
      fields: [
        { key: 'no', label: 'NO' },
        { key: 'name', label: 'NAME' },
        { key: 'status', label: 'STATUS' },
        { key: 'joinedOn', label: 'JOINED ON' },
        { key: 'lastActive', label: 'LAST ACTIVE' },
        { key: 'contact', label: 'CONTACT' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      followers: [],
    }
  },
  computed: {
    advertiserId() {
      return this.$route.params.id
    },
  },
  watch: {
    advertiserId() {
      this.currentPage = 1
      this.fetchFollowers()
    },
  },
  created() {
    this.fetchFollowers()
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.fetchFollowers()
    },
    mapFollowerRow(f, index) {
      return {
        no: f.no ?? index + 1,
        id: f.id,
        name: f.name || f.username || '',
        avatar: resolveMediaUrl(f.avatar || f.profile_image || f.profilephoto),
        status: f.status || 'Active',
        joinedOn: f.joined_on || formatAdminDate(f.joined_date || f.created_at || ''),
        lastActive: f.last_active || f.last_active_at || '',
        contact: f.contact || f.phone || f.email || '',
      }
    },
    async fetchFollowers() {
      if (!this.advertiserId) return
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.advertiserFollowers(this.advertiserId, {
          page: this.currentPage,
          per_page: this.perPage,
        })
        const payload = unwrapAdminResponse(res)
        const list = payload.followers || payload.items || []
        const pagination = normalizePagination(payload, {}, res)
        this.followers = list.map((f, index) => this.mapFollowerRow(f, index))
        this.totalRows = pagination.total
        this.currentPage = pagination.currentPage || this.currentPage
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load followers.'
        this.followers = []
        this.totalRows = 0
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.followers-page {
  padding-top: 0.2rem;
}

::v-deep .followers-table th {
  font-size: 0.76rem;
  color: #6e6b7b;
  letter-spacing: 0.04rem;
}

::v-deep .followers-table td,
::v-deep .followers-table th {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
  vertical-align: middle;
}

::v-deep .page-item.active .page-link {
  background-color: #0b1f39 !important;
  border-color: #0b1f39 !important;
}
</style>
